// tabs/EditTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { Button, Group, SimpleGrid, Stack, Textarea } from '@mantine/core';
import { TextInput, Select, HtmlEditor } from '@/components'; 

import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useACCounsellingServices,
  CareCase,
  useCareNeedService,
  useCareZoneService,
  useLocationService,
  useCareAreaService,
} from '@actschurch/ccms-sdk';

// ─── Props ────────────────────────────────────────────────────────────────────
interface EditTabProps {
  caseID: number;
  caseData: CareCase | null;
  active: boolean;
  onCaseChange: () => void;
}

// ─── Form shape — all Select values stored as strings ────────────────────────
interface EditForm {
  name:        string;
  description: string;
  needID:      string;
  areaID:      string;
  zoneID:      string;
  locationID:  string;
}

const EMPTY_FORM: EditForm = {
  name:        '',
  description: '',
  needID:      '',
  areaID:      '',
  zoneID:      '',
  locationID:  '',
};

export default function EditTab({ caseID, caseData, active, onCaseChange }: EditTabProps) {
  const counsellingService = useACCounsellingServices();

  // Lookup services
  const CareNeeds  = useCareNeedService();
  const CareZones  = useCareZoneService();
  const Locations  = useLocationService();
  const CareAreas  = useCareAreaService();

  // Form state
  const [form, setForm]     = useState<EditForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Lookup lists
  const [needs,     setNeeds]     = useState<any[]>([]);
  const [zones,     setZones]     = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [areas,     setAreas]     = useState<any[]>([]);

  // ── Fetch lookup lists once when tab first becomes active ─────────────────
  const fetchLookups = useCallback(async () => {
    try {
      const [n, z, l, a] = await Promise.all([
        CareNeeds.getAll(),
        CareZones.getAll(),
        Locations.getAll(),
        CareAreas.getAll(),
      ]);
      setNeeds(n     ?? []);
      setZones(z     ?? []);
      setLocations(l ?? []);
      setAreas(a     ?? []);
    } catch (e) {
      console.error('Error fetching lookups:', e);
    }
  }, [CareNeeds, CareZones, Locations, CareAreas]);

  useEffect(() => {
    if (active) fetchLookups();
  }, [active, fetchLookups]);

  // ── Sync form when caseData loads or changes ──────────────────────────────
  useEffect(() => {
    if (!caseData) return;
    setForm({
      name:        caseData.name        ?? '',
      description: caseData.description ?? '',
      needID:      caseData.needID      ? String(caseData.needID)     : '',
      areaID:      caseData.areaID      ? String(caseData.areaID)     : '',
      zoneID:      caseData.zoneID      ? String(caseData.zoneID)     : '',
      locationID:  caseData.locationID  ? String(caseData.locationID) : '',
    });
  }, [caseData]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Validation', 'Case name is required.');
      return;
    }
    if (!form.needID) {
      toast.error('Validation', 'Counseling need is required.');
      return;
    }

    setSaving(true);
    try {
      await counsellingService.careCase.update(caseID, {
        name:        form.name.trim(),
        description: form.description.trim() || undefined,
        needID:      Number(form.needID),
        areaID:      form.areaID     ? Number(form.areaID)     : undefined,
        zoneID:      form.zoneID     ? Number(form.zoneID)     : undefined,
        locationID:  form.locationID ? Number(form.locationID) : undefined,
      } as any);

      toast.success('Saved', 'Case updated successfully.');
      onCaseChange(); // re-fetch in parent so profile card reflects new values
    } catch (e) {
      console.error(e);
      toast.error('Failed', 'Could not save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Stack gap="md">

      {/* ── Name — full width ── */}
      <TextInput
        label="Case Name"
        required
        value={form.name}
        onChange={(e) => setForm((p) => ({ ...p, name: e.currentTarget.value }))}
      />

      {/* ── Description — full width, needs room for the toolbar ── */}
      <HtmlEditor
        label="Description"
        placeholder="Case description"
        value={form.description}
        onHtmlChange={(html) => setForm((p) => ({ ...p, description: html }))}
        minHeight={150}
      />

      {/* ── Lookup fields — paired two-up ── */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Select
          label="Counseling Need"
          placeholder="Select a need"
          required
          data={needs.map((n) => ({
            value: String(n.needID),
            label: n.name ?? `Need ${n.needID}`,
          }))}
          value={form.needID}
          onChange={(v) => setForm((p) => ({ ...p, needID: v ?? '' }))}
          searchable
          clearable
        />

        <Select
          label="Area"
          placeholder="Select an area"
          data={areas.map((a) => ({
            value: String(a.areaID),
            label: a.name ?? `Area ${a.areaID}`,
          }))}
          value={form.areaID}
          onChange={(v) => setForm((p) => ({ ...p, areaID: v ?? '' }))}
          searchable
          clearable
        />

        <Select
          label="Zone"
          placeholder="Select a zone"
          data={zones.map((z) => ({
            value: String(z.zoneID),
            label: z.name ?? `Zone ${z.zoneID}`,
          }))}
          value={form.zoneID}
          onChange={(v) => setForm((p) => ({ ...p, zoneID: v ?? '' }))}
          searchable
          clearable
        />

        <Select
          label="Location"
          placeholder="Select a location"
          data={locations.map((l) => ({
            value: String(l.locationID),
            label: l.name ?? l.locationName ?? `Location ${l.locationID}`,
          }))}
          value={form.locationID}
          onChange={(v) => setForm((p) => ({ ...p, locationID: v ?? '' }))}
          searchable
          clearable
        />
      </SimpleGrid>

      <Group justify="flex-end">
        <Button loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
}